// ============================================================================
// useMediaAutoplay — очередь «слушать подряд» в ленте чата: после окончания
// голосового/аудио автоматически запускается СЛЕДУЮЩЕЕ аудио ленты.
// Инварианты:
//   • автозапуск только аудио (голосовые/аудио), порядок — порядок ленты;
//   • ручная пауза/перемотка/запуск другого медиа отменяют автозапуск
//     (MediaPlayer сообщает onInterrupt);
//   • настройка media.autoplay_next (по умолчанию ВКЛ; тумблер в шапке чата);
//   • «играет только один» — SSoT lib/mediaPlayback (не дублируется здесь).
// Скролл к следующему сообщению — за родителем (Virtuoso): если аудио
// виртуализировано (не смонтировано), MediaPlayer смонтируется после
// прокрутки и выполнит автозапуск по флагу autoPlay.
// ============================================================================

import { useCallback, useMemo, useRef, useState } from 'react';
import { mediaKind } from '@/lib/fileName';
import { isAutoplayNextEnabled } from '@/lib/mediaSettings';

export interface MediaFeedAttachment { id: string; name: string; fileType?: string }
export interface MediaFeedMessage { attachments?: MediaFeedAttachment[] }

interface AudioFeedItem { fileId: string; messageIndex: number }

/** Порядок аудио в ленте (messages уже отсортированы по времени). */
export function buildAudioFeed(messages: MediaFeedMessage[]): AudioFeedItem[] {
  const items: AudioFeedItem[] = [];
  messages.forEach((m, messageIndex) => {
    for (const a of m.attachments || []) {
      if (mediaKind(a.name, a.fileType) === 'audio') items.push({ fileId: a.id, messageIndex });
    }
  });
  return items;
}

export function useMediaAutoplay(
  messages: MediaFeedMessage[],
  /** Прокрутить ленту к сообщению; false — лента отфильтрована (поиск),
   *  координаты ненадёжны → автозапуск не строим (детерминизм). */
  scrollToMessage: (messageIndex: number) => boolean,
): {
  /** fileId, которому очередь просит автозапуск (null — очередь свободна) */
  autoplayFileId: string | null;
  handleAudioEnded: (fileId: string) => void;
  handleAudioInterrupt: () => void;
  handleAutoplayConsumed: (fileId: string) => void;
} {
  const feed = useMemo(() => buildAudioFeed(messages), [messages]);
  // Актуальный порядок ленты — в ref: обработчик конца воспроизведения
  // вызывается из события медиа-элемента (без пересоздания колбэка).
  const feedRef = useRef(feed);
  feedRef.current = feed;
  const scrollRef = useRef(scrollToMessage);
  scrollRef.current = scrollToMessage;
  const [autoplayFileId, setAutoplayFileId] = useState<string | null>(null);

  const handleAudioEnded = useCallback((fileId: string) => {
    if (!isAutoplayNextEnabled()) return;
    const items = feedRef.current;
    const idx = items.findIndex((x) => x.fileId === fileId);
    if (idx < 0) return;
    const next = items[idx + 1];
    if (!next) return;
    if (!scrollRef.current(next.messageIndex)) return;
    setAutoplayFileId(next.fileId);
  }, []);

  const handleAudioInterrupt = useCallback(() => setAutoplayFileId(null), []);

  const handleAutoplayConsumed = useCallback((fileId: string) => {
    setAutoplayFileId((cur) => (cur === fileId ? null : cur));
  }, []);

  return { autoplayFileId, handleAudioEnded, handleAudioInterrupt, handleAutoplayConsumed };
}
